import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import {
  X, Clock, PaperPlaneRight, CircleNotch, WarningCircle, Users, EnvelopeSimple,
  Signature, FlowArrow, Info, CheckCircle, Warning,
} from '@phosphor-icons/react'
import { campaignsApi, getErrorMessage, type PendingCampaignApproval } from '@/lib/api'
import type { CampaignReview } from '@/types/campaign'
import ReadOnlyWorkflowCanvas from '@/components/workflow/ReadOnlyWorkflowCanvas'
import DecisionFooter from '@/components/approvals/DecisionFooter'
import { nodeSummary } from '@/lib/workflowNodeMeta'
import { newlinesToBr, hasSignatureContent } from '@/lib/signatureHtml'
import { useScrollLock } from '@/lib/useScrollLock'
import { warnBannerClass, bannerTextStyle } from '@/lib/styleKit'

/**
 * Settings > Campaign Approvals: the full in-place review for one launch
 * request (brief C4). Everything the campaign will do is laid out in
 * sections (audience, sender inbox, signature, workflow, details) from
 * GET /campaigns/{id}/review, so the approver never has to open the
 * campaign itself. When the workflow is not yet active, approving the
 * campaign activates it too, and the modal says so in amber.
 */
interface Props {
  request: PendingCampaignApproval | null
  onClose: () => void
  onApprove: () => Promise<void>
  onReject: (reason: string) => Promise<void>
}

export function formatReviewDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return format(d, 'd MMM yyyy, HH:mm')
}

/** "pending_approval" -> "Pending approval". */
export function humanizeStatus(status: string | null | undefined): string {
  if (!status) return 'Unknown'
  const words = status.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const chipClass =
  'inline-flex items-center gap-1 rounded-full border border-border/60 dark:border-white/[0.1] ' +
  'bg-muted/50 dark:bg-white/[0.04] px-2.5 py-0.5 text-foreground dark:text-[#e2e4e7]'

const approvedChipClass =
  'inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/[0.10] ' +
  'px-2.5 py-0.5 text-emerald-700 dark:text-emerald-300'

const bodyTextStyle: React.CSSProperties = { fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }
const metaTextStyle: React.CSSProperties = { fontSize: '12px', letterSpacing: '-0.1px' }

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <section
    className="rounded-lg border border-border/50 dark:border-white/[0.08] bg-muted/30 dark:bg-white/[0.02] px-4 py-3"
    aria-label={title}
  >
    <h3
      className="mb-2 flex items-center gap-1.5 text-foreground dark:text-[#f7f8f8]"
      style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
    >
      <span className="text-[#5e6ad2] dark:text-[#a5b4fc]">{icon}</span>
      {title}
    </h3>
    {children}
  </section>
)

const CampaignReviewModal: React.FC<Props> = ({ request, onClose, onApprove, onReject }) => {
  const open = request != null
  const campaignId = request?.campaign_id ?? null
  const [review, setReview] = useState<CampaignReview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useScrollLock(open)

  useEffect(() => {
    if (campaignId == null) return
    let cancelled = false
    setReview(null)
    setError(null)
    setLoading(true)
    campaignsApi
      .review(campaignId)
      .then(res => {
        if (!cancelled) setReview(res.data)
      })
      .catch(err => {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load this campaign'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [campaignId])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, busy, onClose])

  if (!request) return null

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
      onClose()
    } catch {
      // The store already toasted; stay open so the approver can retry.
    } finally {
      setBusy(false)
    }
  }

  const title = review?.campaign.name || request.campaign_name
  const requestedAt = formatReviewDate(request.requested_at)

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label={`Review campaign ${title}`}
    >
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={busy ? undefined : onClose}
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl border border-border/60 bg-card shadow-2xl dark:border-white/[0.08] dark:bg-[#0f1011]"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
          <div className="min-w-0">
            <p
              className="text-muted-foreground dark:text-[#8a8f98] uppercase"
              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em' }}
            >
              Campaign launch request
            </p>
            <h2
              className="text-foreground dark:text-[#f7f8f8] truncate"
              style={{ fontSize: '18px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
            >
              {title}
            </h2>
            <div
              className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground dark:text-[#8a8f98]"
              style={metaTextStyle}
            >
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Requested by {request.requested_by_name}
                {requestedAt ? ` on ${requestedAt}` : ''}
              </span>
              {request.approver_name && (
                <span className="inline-flex items-center gap-1.5">
                  <PaperPlaneRight className="h-3 w-3" />
                  Sent to {request.approver_name}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close review"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
          >
            <X className="h-4 w-4" weight="regular" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 space-y-3">
          {loading && (
            <div
              className="flex items-center gap-2 py-10 justify-center text-muted-foreground dark:text-[#8a8f98]"
              style={bodyTextStyle}
              role="status"
            >
              <CircleNotch className="h-4 w-4 animate-spin" />
              Loading campaign
            </div>
          )}
          {error && !loading && (
            <p className="flex items-center gap-2 text-red-700 dark:text-red-300" style={bodyTextStyle} role="alert">
              <WarningCircle className="h-4 w-4" weight="fill" />
              {error}
            </p>
          )}
          {review && !loading && (
            <>
              <Section icon={<Users className="h-4 w-4" weight="fill" />} title="Audience">
                {review.audience.mode === 'all' || review.audience.lists.length === 0 ? (
                  <p className="text-foreground dark:text-[#e2e4e7]" style={bodyTextStyle}>
                    All contacts
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {review.audience.lists.map(list => (
                      <li
                        key={list.id}
                        className="flex items-center justify-between gap-3 text-foreground dark:text-[#e2e4e7]"
                        style={bodyTextStyle}
                      >
                        <span className="truncate">{list.name}</span>
                        <span className="text-muted-foreground dark:text-[#8a8f98] flex-shrink-0" style={metaTextStyle}>
                          {list.contact_count.toLocaleString()} contact{list.contact_count === 1 ? '' : 's'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-muted-foreground dark:text-[#8a8f98]" style={metaTextStyle}>
                  {review.audience.total_recipients.toLocaleString()} total recipient
                  {review.audience.total_recipients === 1 ? '' : 's'}
                </p>
              </Section>

              <Section icon={<EnvelopeSimple className="h-4 w-4" weight="fill" />} title="Sender inbox">
                {review.senders.length === 0 ? (
                  <p className="text-muted-foreground dark:text-[#8a8f98]" style={bodyTextStyle}>
                    No sender inbox selected
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {review.senders.map(sender => (
                      <li key={sender.id} className="text-foreground dark:text-[#e2e4e7]" style={bodyTextStyle}>
                        <span style={{ fontWeight: 510 }}>
                          {sender.label || sender.display_name || sender.email_address}
                        </span>
                        <span className="text-muted-foreground dark:text-[#8a8f98]"> {sender.email_address}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={<Signature className="h-4 w-4" weight="fill" />} title="Signature">
                {review.signature.source === 'none' || !hasSignatureContent(review.signature.html) ? (
                  <p
                    className="flex items-center gap-1.5 text-amber-700 dark:text-amber-300"
                    style={bodyTextStyle}
                  >
                    <Warning className="h-3.5 w-3.5" weight="fill" />
                    No signature set
                  </p>
                ) : (
                  <>
                    <p className="mb-2 text-muted-foreground dark:text-[#8a8f98]" style={metaTextStyle}>
                      {review.signature.source === 'campaign'
                        ? `Campaign signature: ${review.signature.name || 'Untitled'}`
                        : 'Mailbox signature'}
                    </p>
                    <div className="rounded-lg border border-dashed border-border/60 dark:border-white/[0.08] bg-background dark:bg-white/[0.02] p-3">
                      <div
                        className="text-sm text-foreground dark:text-[#e2e4e7]"
                        style={{ fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.5 }}
                        data-testid="signature-preview"
                        dangerouslySetInnerHTML={{ __html: newlinesToBr(review.signature.html) }}
                      />
                    </div>
                  </>
                )}
              </Section>

              <Section icon={<FlowArrow className="h-4 w-4" weight="fill" />} title="Workflow">
                {!review.workflow && (
                  <p className="text-muted-foreground dark:text-[#8a8f98]" style={bodyTextStyle}>
                    This workflow no longer exists.
                  </p>
                )}
                {review.workflow && (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-foreground dark:text-[#e2e4e7]" style={{ ...bodyTextStyle, fontWeight: 510 }}>
                    {review.workflow.name}
                  </span>
                  {review.workflow.is_approved && (
                    <span className={approvedChipClass} style={metaTextStyle}>
                      <CheckCircle className="h-3.5 w-3.5" weight="fill" />
                      Approved
                    </span>
                  )}
                </div>
                )}
                {review.workflow && !review.workflow.is_approved && (
                  <>
                    <div className={`${warnBannerClass} mb-3`} style={bannerTextStyle}>
                      <Warning className="h-4 w-4 flex-shrink-0 mt-px" weight="fill" />
                      <span>Approving this campaign also activates this workflow</span>
                    </div>
                    <ReadOnlyWorkflowCanvas nodes={review.workflow.nodes} edges={review.workflow.edges} height={320} />
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="node-summary">
                      {nodeSummary(review.workflow.nodes).map(row => (
                        <span key={row.type} className={chipClass} style={metaTextStyle}>
                          {row.count} {row.kind}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </Section>

              <Section icon={<Info className="h-4 w-4" weight="fill" />} title="Campaign details">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1" style={bodyTextStyle}>
                  <dt className="text-muted-foreground dark:text-[#8a8f98]">Name</dt>
                  <dd className="text-foreground dark:text-[#e2e4e7]">{review.campaign.name}</dd>
                  <dt className="text-muted-foreground dark:text-[#8a8f98]">Description</dt>
                  <dd className="text-foreground dark:text-[#e2e4e7]">
                    {review.campaign.description?.trim() ? review.campaign.description : 'No description'}
                  </dd>
                  <dt className="text-muted-foreground dark:text-[#8a8f98]">Scheduled start</dt>
                  <dd className="text-foreground dark:text-[#e2e4e7]">
                    {formatReviewDate(review.campaign.scheduled_start) ?? 'Not scheduled'}
                  </dd>
                  <dt className="text-muted-foreground dark:text-[#8a8f98]">Status</dt>
                  <dd className="text-foreground dark:text-[#e2e4e7]">{humanizeStatus(review.campaign.status)}</dd>
                </dl>
              </Section>
            </>
          )}
        </div>

        <div className="border-t border-border/50 dark:border-white/[0.06] px-6 py-4">
          <DecisionFooter
            approveLabel="Validate & approve"
            onApprove={() => run(onApprove)}
            onReject={reason => run(() => onReject(reason))}
            busy={busy}
          />
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}

export default CampaignReviewModal
